import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useJsApiLoader, Autocomplete } from '@react-google-maps/api';
import { cn } from '../../lib/utils';

const { FiSave, FiX, FiFolder, FiCalendar, FiImage, FiPlus, FiTrash2, FiMapPin, FiTag, FiChevronDown } = FiIcons;

const libraries = ['places'];

const EntryForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [autocomplete, setAutocomplete] = useState(null);
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries
  });

  const [formData, setFormData] = useState({
    title: '',
    section: 'luka',
    date: new Date().toISOString().split('T')[0],
    folder_name: 'General',
    excerpt: '',
    body: '',
    cover_image_url: '',
    location_text: '',
    lat: null,
    lng: null,
    tags: [],
    status: 'published'
  });

  const [tagInput, setTagInput] = useState('');
  const [mediaItems, setMediaItems] = useState([]);

  useEffect(() => {
    if (id) fetchEntry();
  }, [id]);

  const fetchEntry = async () => {
    const { data: entry } = await supabase.from('entries_20240520').select('*').eq('id', id).single();
    if (entry) {
      setFormData({
        ...entry,
        tags: Array.isArray(entry.tags) ? entry.tags : [],
        lat: entry.lat || null,
        lng: entry.lng || null
      });
      const { data: media } = await supabase.from('media_20240520').select('*').eq('entry_id', id).order('sort_order');
      setMediaItems(media || []);
    }
  };

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setFormData({ ...formData, location_text: place.formatted_address || place.name, lat, lng });
    }
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const tag = tagInput.trim().toLowerCase();
      if (tag && !formData.tags.includes(tag)) {
        setFormData({ ...formData, tags: [...formData.tags, tag] });
      }
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tagToRemove) });
  };

  const updateMediaItem = (index, field, value) => {
    const newItems = [...mediaItems];
    newItems[index][field] = value;
    setMediaItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const dataToSave = { ...formData, updated_by: user.id };

    try {
      let entryId = id;
      if (id) {
        const { error } = await supabase.from('entries_20240520').update(dataToSave).eq('id', id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from('entries_20240520').insert(dataToSave).select().single();
        if (error) throw error;
        entryId = data.id;
      }

      if (id) await supabase.from('media_20240520').delete().eq('entry_id', id);
      const validMedia = mediaItems.filter(item => item.url.trim() !== '');
      if (validMedia.length > 0) {
        await supabase.from('media_20240520').insert(
          validMedia.map((item, idx) => ({ entry_id: entryId, ...item, sort_order: idx }))
        );
      }

      toast.success(id ? 'Memory Updated' : 'Memory Posted!');
      navigate('/admin/entries');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <div className="flex justify-between items-center mb-10">
        <div>
          <h1 className="text-4xl font-serif font-bold text-[#2c2c2c]">{id ? 'Edit Memory' : 'Create New Memory'}</h1>
          <p className="text-stone-500 mt-1 italic">Preserving the Dimovski family legacy.</p>
        </div>
        <button onClick={() => navigate(-1)} className="p-3 bg-white border border-stone-200 rounded-full hover:bg-stone-50 shadow-sm transition-all">
          <SafeIcon icon={FiX} className="w-6 h-6 text-stone-400" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-10 pb-32">
        {/* Core Info */}
        <section className="bg-white p-10 rounded-[2.5rem] border border-stone-200 shadow-sm space-y-8">
          <div className="space-y-2">
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8c7851]">Memory Title</label>
            <input 
              required 
              value={formData.title} 
              onChange={e => setFormData({ ...formData, title: e.target.value })} 
              className="w-full text-3xl font-serif font-bold border-none bg-stone-50 px-6 py-4 rounded-2xl focus:ring-4 focus:ring-[#8c7851]/5 outline-none transition-all placeholder:text-stone-200"
              placeholder="Give this moment a name..." 
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8c7851]">Archive Section</label>
              <div className="relative group">
                <select 
                  value={formData.section} 
                  onChange={e => setFormData({ ...formData, section: e.target.value })} 
                  className="w-full appearance-none px-6 py-5 rounded-2xl border-2 border-stone-100 bg-white font-bold text-stone-700 focus:border-[#8c7851] outline-none transition-all cursor-pointer"
                >
                  <option value="luka">Luka's Collection</option>
                  <option value="klara">Klara's Collection</option>
                  <option value="family">Family Shared</option>
                  <option value="trips">Travel & Trips</option>
                </select>
                <SafeIcon icon={FiChevronDown} className="absolute right-5 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none group-hover:text-[#8c7851] transition-colors" />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8c7851] flex items-center gap-2">
                <SafeIcon icon={FiCalendar} /> Date of Memory
              </label>
              <input 
                type="date" 
                required 
                value={formData.date} 
                onChange={e => setFormData({ ...formData, date: e.target.value })} 
                className="w-full px-6 py-5 rounded-2xl border-2 border-stone-100 bg-white font-bold text-stone-700 focus:border-[#8c7851] outline-none transition-all text-lg"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8c7851] flex items-center gap-2">
              <SafeIcon icon={FiMapPin} /> Recorded Location
            </label>
            {isLoaded ? (
              <Autocomplete onLoad={setAutocomplete} onPlaceChanged={onPlaceChanged}>
                <input 
                  type="text" 
                  value={formData.location_text} 
                  onChange={(e) => setFormData({ ...formData, location_text: e.target.value })} 
                  className="w-full px-6 py-4 rounded-2xl border-2 border-stone-100 bg-white focus:border-[#8c7851] outline-none transition-all"
                  placeholder="Where did this happen?" 
                />
              </Autocomplete>
            ) : (
              <div className="w-full h-14 bg-stone-50 rounded-2xl animate-pulse" />
            )}
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8c7851] flex items-center gap-2">
              <SafeIcon icon={FiTag} /> Memory Tags
            </label>
            <div className="p-4 rounded-2xl border-2 border-stone-100 bg-white min-h-[60px] flex flex-wrap gap-2 items-center">
              {formData.tags.map(tag => (
                <span key={tag} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#8c7851]/10 text-[#8c7851] text-xs font-bold rounded-lg border border-[#8c7851]/20">
                  #{tag}
                  <button type="button" onClick={() => removeTag(tag)} className="hover:text-red-500 font-bold transition-colors">
                    <SafeIcon icon={FiX} className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <input 
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={handleAddTag}
                placeholder={formData.tags.length === 0 ? "Add tags (press enter or comma)..." : ""}
                className="flex-grow min-w-[150px] border-none outline-none focus:ring-0 text-sm py-1 font-medium bg-transparent"
              />
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="bg-white p-10 rounded-[2.5rem] border border-stone-200 shadow-sm space-y-6">
          <div className="space-y-2">
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8c7851]">Main Story</label>
            <textarea 
              required 
              value={formData.body} 
              onChange={e => setFormData({ ...formData, body: e.target.value })} 
              className="w-full px-8 py-8 rounded-[2rem] border-2 border-stone-100 bg-stone-50 min-h-[400px] text-xl font-serif leading-relaxed focus:bg-white focus:border-[#8c7851] outline-none transition-all" 
              placeholder="Tell the story of this memory in detail..." 
            />
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8c7851]">Cover Photo URL</label>
            <div className="relative group">
              <SafeIcon icon={FiImage} className="absolute left-5 top-1/2 -translate-y-1/2 text-stone-300" />
              <input 
                value={formData.cover_image_url} 
                onChange={e => setFormData({ ...formData, cover_image_url: e.target.value })} 
                className="w-full pl-14 pr-6 py-4 rounded-2xl border-2 border-stone-100 bg-white focus:border-[#8c7851] outline-none transition-all" 
                placeholder="Paste an image link from Google Photos, Dropbox, etc." 
              />
            </div>
          </div>
        </section>

        {/* Gallery Section */}
        <section className="bg-stone-50 p-10 rounded-[2.5rem] border border-stone-200">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="font-serif font-bold text-2xl text-[#2c2c2c]">Additional Gallery</h2>
              <p className="text-xs text-stone-400 mt-1">Add more photos to create a collection of moments.</p>
            </div>
            <button 
              type="button" 
              onClick={() => setMediaItems([...mediaItems, { url: '', type: 'image', caption: '', sort_order: mediaItems.length }])} 
              className="px-5 py-2.5 bg-white border border-stone-200 rounded-xl text-sm font-bold text-[#8c7851] flex items-center gap-2 hover:bg-stone-100 transition-all shadow-sm"
            >
              <SafeIcon icon={FiPlus} /> Add Photo URL
            </button>
          </div>
          <div className="space-y-4">
            {mediaItems.map((item, index) => (
              <div key={index} className="flex gap-4 p-5 bg-white rounded-2xl border border-stone-100 items-center shadow-sm">
                <div className="w-10 h-10 bg-stone-50 rounded-lg flex items-center justify-center text-stone-300 font-bold text-xs">{index + 1}</div>
                <input 
                  placeholder="Paste direct Image URL here..." 
                  value={item.url} 
                  onChange={e => updateMediaItem(index, 'url', e.target.value)} 
                  className="flex-1 px-4 py-3 rounded-xl border border-stone-100 text-sm focus:border-[#8c7851] outline-none transition-all" 
                />
                <button 
                  type="button" 
                  onClick={() => setMediaItems(mediaItems.filter((_, i) => i !== index))} 
                  className="p-3 text-red-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                >
                  <SafeIcon icon={FiTrash2} />
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Sticky Actions */}
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 w-full max-w-4xl px-4 z-[100]">
          <div className="bg-white/80 backdrop-blur-xl p-4 rounded-[2rem] border border-stone-200 shadow-2xl flex gap-4">
            <button 
              type="button" 
              onClick={() => navigate(-1)} 
              className="flex-1 px-8 py-4 rounded-2xl text-sm font-bold border-2 border-stone-100 hover:bg-stone-50 transition-all"
            >
              Cancel Changes
            </button>
            <button 
              type="submit" 
              disabled={loading} 
              className="flex-[2] bg-[#2c2c2c] text-white px-10 py-4 rounded-2xl font-bold flex items-center justify-center gap-3 hover:bg-black transition-all shadow-lg active:scale-[0.98] disabled:opacity-50"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
              ) : (
                <SafeIcon icon={FiSave} className="w-5 h-5" />
              )}
              <span>{id ? 'Update Memory' : 'Preserve Memory'}</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EntryForm;